import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { getSignerContractByAddress, parseAmount, getTokenSymbol } from '../contracts/contract';
import { NetworkConfig } from '../config/networks';
import './DepositForm.css';

interface DepositFormProps {
  signer: ethers.Signer;
  network: NetworkConfig;
  userAddress: string;
  provider: ethers.Provider;
  contractAddress: string;
  onDeposit: () => void;
}

const DepositForm: React.FC<DepositFormProps> = ({ signer, network, userAddress, provider, contractAddress, onDeposit }) => {
  const [tokenAddress, setTokenAddress] = useState('');
  const [beneficiary, setBeneficiary] = useState('');
  const [amount, setAmount] = useState('');
  const [isEthDeposit, setIsEthDeposit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [tokenSymbol, setTokenSymbol] = useState<string>('Token');

  // Fetch token symbol when token address changes
  useEffect(() => {
    const fetchTokenSymbol = async () => {
      if (!provider || !tokenAddress || isEthDeposit) {
        setTokenSymbol('Token');
        return;
      }

      try {
        const symbol = await getTokenSymbol(provider, tokenAddress);
        setTokenSymbol(symbol);
      } catch (error) {
        console.warn(`Failed to fetch symbol for ${tokenAddress}:`, error);
        setTokenSymbol('Token');
      }
    };

    fetchTokenSymbol();
  }, [provider, tokenAddress, isEthDeposit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const contract = getSignerContractByAddress(signer, contractAddress);
      
      // Trustline validation for deposits is disabled
      // setLoadingMessage('Validating transaction with Trustline...');
      // const tokenAddressForValidation = isEthDeposit ? ethers.ZeroAddress : tokenAddress;
      // const amountForValidation = isEthDeposit ? ethers.parseEther(amount).toString() : parseAmount(amount);
      
      // const isApproved = await trustlineService.validateDeposit(
      //   network.chainId,
      //   userAddress,
      //   network.contractAddress,
      //   tokenAddressForValidation,
      //   amountForValidation,
      //   beneficiary
      // );

      // if (!isApproved) {
      //   throw new Error('Transaction not approved by Trustline compliance check');
      // }

      if (isEthDeposit) {
        // ETH deposit
        setLoadingMessage(`Processing ${network.nativeCurrency.symbol} deposit...`);
        const value = ethers.parseEther(amount);
        const tx = await contract.depose(ethers.ZeroAddress, beneficiary, value, { value });
        await tx.wait();
      } else {
        // ERC20 deposit
        if (!tokenAddress) {
          throw new Error('Token address is required for ERC20 deposits');
        }
        
        // First approve the contract to spend tokens
        const tokenContract = new ethers.Contract(
          tokenAddress,
          ['function approve(address spender, uint256 amount) returns (bool)'],
          signer
        );
        
        const tokenAmount = parseAmount(amount);
        
        // Step 1: Send approve transaction
        setLoadingMessage('Approving token spending...');
        const approveTx = await tokenContract.approve(contractAddress, tokenAmount);
        
        // Step 2: Wait for approve transaction to be confirmed
        setLoadingMessage('Waiting for approval confirmation...');
        console.log('Waiting for approval transaction to be confirmed...');
        const approveReceipt = await approveTx.wait();
        console.log('Approval transaction confirmed:', approveReceipt.transactionHash);
        
        // Step 2.5: Small delay to ensure approval state is updated
        setLoadingMessage('Finalizing approval...');
        await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay
        
        // Step 3: Now deposit the tokens
        setLoadingMessage('Creating token deposit...');
        const depositTx = await contract.depose(tokenAddress, beneficiary, tokenAmount);
        const depositReceipt = await depositTx.wait();
        console.log('Deposit transaction confirmed:', depositReceipt.transactionHash);
      }
      
      // Reset form
      setAmount('');
      setBeneficiary('');
      setTokenAddress('');
      setIsEthDeposit(true);
      setLoadingMessage('');
      
      // Refresh deposits
      onDeposit();
    } catch (err) {
      setError('Transaction failed: ' + (err as Error).message);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  return (
    <div className="deposit-form">
      <h3>Create New Deposit</h3>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group radio-group">
          <label>
            <input
              type="radio"
              checked={isEthDeposit}
              onChange={() => setIsEthDeposit(true)}
            />
            {network.nativeCurrency.symbol} Deposit
          </label>
          <label>
            <input
              type="radio"
              checked={!isEthDeposit}
              onChange={() => setIsEthDeposit(false)}
            />
            ERC20 Token Deposit
          </label>
        </div>

        {!isEthDeposit && (
          <div className="form-group">
            <label htmlFor="tokenAddress">Token Address:</label>
            <input
              id="tokenAddress"
              type="text"
              value={tokenAddress}
              onChange={(e) => setTokenAddress(e.target.value)}
              placeholder="0x..."
              required={!isEthDeposit}
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="beneficiary">Beneficiary Address:</label>
          <input
            id="beneficiary"
            type="text"
            value={beneficiary}
            onChange={(e) => setBeneficiary(e.target.value)}
            placeholder="0x..."
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="amount">Amount:</label>
          <div className="amount-input-wrapper">
            <input
              id="amount"
              type="number"
              step="0.000001"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.0"
              required
            />
            <span className="amount-unit">{isEthDeposit ? network.nativeCurrency.symbol : tokenSymbol}</span>
          </div>
        </div>

        <button type="submit" disabled={loading} className="submit-btn">
          {loading ? (loadingMessage || 'Processing...') : 'Create Deposit'}
        </button>

        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
};

export default DepositForm;
